import { __awaiter } from "tslib";
import React, { Fragment, useEffect, useState } from "react";
import { Alert, AlertTitle, Box, Button, Dialog, DialogContent, Divider, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { styles } from "./theme";
import { Language, resources } from "./resources";
import { ErrorCode, PaymentProvider, PaymentStatus, PaymentType } from "./types";
import { env, fetchStripeClientSecret, getPayeeCountry, getPayeeName, listPaymentMethods, renewWalleePayment, retrievePayment, saveStripePaymentMethod } from "./request";
import { CheckCircleOutlineRoundedIcon, HighlightOffIcon, HourglassEmptyRoundedIcon } from "./icons";
import { PaymentMethodList, PaymentView } from "./flow/payment";
const useStyles = makeStyles({
    dialogContent: {
        overflowY: 'visible'
    }
});
export const PaymentFlow = ({ paymentId, onClose, onSucceeded, onProcessing, onError, live, smallDevice, language }) => {
    const smallDisplay = window.innerWidth <= 480 || smallDevice;
    env.stage = (live) ? 'live' : 'dev';
    env.paymentId = paymentId;
    const classes = useStyles();
    return (React.createElement(Dialog, { open: true, keepMounted: true, fullScreen: smallDisplay, maxWidth: "sm" },
        React.createElement(DialogContent, { id: "payment-flow-box", style: (smallDisplay ? styles.contentWrapper : styles.contentBoxed), classes: { root: classes.dialogContent } },
            React.createElement(Box, { px: smallDisplay ? 0 : 2, py: 2, style: { flexGrow: 1 } },
                React.createElement(PaymentShell, { onClose: onClose, onError: (onError) ? onError : () => { }, onProcessing: (onProcessing) ? onProcessing : (_p) => { }, onSucceeded: onSucceeded, language: language })))));
};
var PaymentStateMachine;
(function (PaymentStateMachine) {
    PaymentStateMachine[PaymentStateMachine["InitiatePayment"] = 0] = "InitiatePayment";
    PaymentStateMachine[PaymentStateMachine["InvalidPayment"] = 1] = "InvalidPayment";
    PaymentStateMachine[PaymentStateMachine["AwaitPaymentMethodSelection"] = 2] = "AwaitPaymentMethodSelection";
    PaymentStateMachine[PaymentStateMachine["RenderPaymentView"] = 3] = "RenderPaymentView";
    PaymentStateMachine[PaymentStateMachine["PaymentResult"] = 4] = "PaymentResult";
})(PaymentStateMachine || (PaymentStateMachine = {}));
export const PaymentShell = ({ onClose, onProcessing, onSucceeded, onError, language }) => {
    const [stateMachine, setStateMachine] = useState(PaymentStateMachine.InitiatePayment);
    const [payment, setPayment] = useState();
    const [error, setError] = useState();
    const [payeeName, setPayeeName] = useState('');
    const [payeeCountry, setPayeeCountry] = useState('');
    const [paymentMethod, setPaymentMethod] = useState();
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [stripeClientSecret, setStripeClientSecret] = useState();
    const [busy, setBusy] = useState(false);
    const [walleePaymentRenewalError, setWalleePaymentRenewalError] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const sendError = (error) => {
        onError(error);
        setError(error.message);
    };
    const getPayment = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            return yield retrievePayment(env.paymentId);
        }
        catch (error) {
            console.error("An error occurred: ", error);
            sendError(error);
        }
        return null;
    });
    useEffect(() => {
        if (!!language) {
            let parsed = language.substring(0, 2).toLowerCase();
            if (Object.values(Language).map(l => l.toString()).includes(parsed))
                resources.language = parsed;
        }
        getPayment()
            .then((p) => __awaiter(void 0, void 0, void 0, function* () {
            if (p) {
                const payeeName = yield getPayeeName(env.paymentId);
                const payeeCountry = yield getPayeeCountry(env.paymentId);
                setPayeeName(payeeName);
                setPayeeCountry(payeeCountry);
                yield handlePayment(p);
                if (p.status === PaymentStatus.Processing) {
                    reloadPayment();
                }
                return;
            }
            setStateMachine(PaymentStateMachine.InvalidPayment);
        }));
    }, []);
    const reloadPayment = () => {
        const interval = setInterval(() => {
            getPayment()
                .then(p => {
                if (p && p.status !== PaymentStatus.Processing) {
                    clearInterval(interval);
                    handlePayment(p)
                        .then();
                }
            });
        }, 5000);
    };
    const handlePayment = (payment) => __awaiter(void 0, void 0, void 0, function* () {
        const stateMachine = (payment.status === PaymentStatus.Pending || payment.status === PaymentStatus.Failed) ?
            PaymentStateMachine.AwaitPaymentMethodSelection :
            PaymentStateMachine.PaymentResult;
        if (stateMachine === PaymentStateMachine.AwaitPaymentMethodSelection) {
            const paymentMethods = yield listPaymentMethods(env.paymentId);
            setPaymentMethods(paymentMethods);
        }
        setPayment(payment);
        setStateMachine(stateMachine);
        if (payment.status === PaymentStatus.Processing) {
            onProcessing(payment);
        }
        else if (payment.status === PaymentStatus.Succeeded) {
            onSucceeded(payment);
        }
    });
    const handlePaymentMethodSelected = (paymentMethod, useStoredMethod) => __awaiter(void 0, void 0, void 0, function* () {
        if (paymentMethod.stripe && paymentMethod.stripe.type !== PaymentType.PaymentRequest) {
            try {
                const stripeClientSecret = yield fetchStripeClientSecret(env.paymentId, (useStoredMethod) ? paymentMethod.stripe.methodId : undefined);
                setStripeClientSecret(stripeClientSecret);
                setStateMachine(PaymentStateMachine.RenderPaymentView);
            }
            catch (error) {
                console.error("An error occurred: ", error);
                sendError(error);
                return;
            }
        }
        setPaymentMethod(paymentMethod);
    });
    const handlePaymentConfirmed = (status, method, saveMethod) => __awaiter(void 0, void 0, void 0, function* () {
        if (payment) {
            if (method.stripe) {
                payment.type = method.stripe.type;
            }
            else if (method.paypal) {
                payment.type = PaymentType.PayPal;
            }
            payment.status = status;
            setPayment(payment);
            setStateMachine(PaymentStateMachine.PaymentResult);
            if (saveMethod) {
                try {
                    yield saveStripePaymentMethod(env.paymentId);
                }
                catch (error) {
                    console.error('Failed save Stripe payment method, error: ', error);
                    sendError(error);
                }
            }
            console.log('switch status', status);
            switch (status) {
                case PaymentStatus.Succeeded:
                    // Give the backend enough time to catch and execute VCP Callback
                    window.setTimeout(() => {
                        onSucceeded(payment);
                    }, 2000);
                    break;
                case PaymentStatus.Processing:
                    onProcessing(payment);
                    yield reloadPayment();
                    break;
                default:
                    break;
            }
        }
    });
    const handlePaymentBack = () => {
        setStateMachine(PaymentStateMachine.AwaitPaymentMethodSelection);
    };
    function handleWalleePaymentRenewal() {
        return __awaiter(this, void 0, void 0, function* () {
            if (payment) {
                setInProgress(true);
                try {
                    const newPayment = yield renewWalleePayment(env.paymentId);
                    yield handlePayment(newPayment);
                }
                catch (err) {
                    console.error('Failed to renew wallee payment', err);
                    setWalleePaymentRenewalError(true);
                }
                setInProgress(false);
            }
        });
    }
    const renderAmount = (payment) => {
        let currencySymbol;
        switch (payment.currency) {
            case 'EUR':
                currencySymbol = '€';
                break;
            case 'USD':
                currencySymbol = '$';
                break;
            case 'CHF':
                currencySymbol = 'CHF';
                break;
            default:
                currencySymbol = payment.currency;
                break;
        }
        const [number, decimal] = payment.amount.toFixed(2).split('.');
        return `${currencySymbol} ${number},${decimal}`;
    };
    const renderCloseButton = () => {
        const messages = resources.getStrings();
        let cancelText = (stateMachine === PaymentStateMachine.InvalidPayment || stateMachine === PaymentStateMachine.PaymentResult) ?
            'OK' :
            messages.Cancel;
        return (React.createElement(Box, { style: { textAlign: 'center' }, mt: 2 },
            React.createElement(Button, { onClick: onClose, variant: "contained", color: "primary", disabled: busy }, cancelText)));
    };
    const renderErrorCode = (errorCode) => {
        const messages = resources.getStrings();
        const error = Object.values(ErrorCode).findIndex(c => c === errorCode);
        const errorMessage = (error) ? messages['ErrorCode' + Object.keys(ErrorCode)[error]] : (messages.ErrorOccurred + ' ' + errorCode);
        if (!errorMessage)
            return React.createElement("div", null);
        return (React.createElement(Alert, { severity: "error", style: { textAlign: 'center' } }, errorMessage));
    };
    const messages = resources.getStrings();
    return (React.createElement("div", null,
        stateMachine === PaymentStateMachine.InitiatePayment && (React.createElement(Typography, { variant: "body1", align: "center" }, messages.InitiatePayment)),
        stateMachine === PaymentStateMachine.InvalidPayment && (React.createElement("div", null,
            React.createElement(Typography, { variant: "body1", align: "center" }, messages.InvalidPayment),
            renderCloseButton())),
        payment && (React.createElement("div", null,
            React.createElement(Typography, { variant: "h6", align: "center" },
                messages.Amount,
                ": ",
                renderAmount(payment),
                " "),
            React.createElement(Box, { my: 2, children: React.createElement(Divider, null) }))),
        stateMachine === PaymentStateMachine.AwaitPaymentMethodSelection && (React.createElement("div", null,
            payment && (React.createElement(Fragment, null,
                React.createElement(PaymentMethodList, { paymentMethods: paymentMethods, payeeName: payeeName, country: payeeCountry, amount: payment.amount, currency: payment.currency, onPaymentError: sendError, onPaymentConfirmed: handlePaymentConfirmed, onSelect: handlePaymentMethodSelected, onBusy: setBusy }),
                (payment.status === PaymentStatus.Failed) && (React.createElement(Box, { mt: 2 },
                    React.createElement(Alert, { severity: "warning", style: { textAlign: 'center' } }, messages.PaymentFailed))))),
            renderCloseButton())),
        (stateMachine === PaymentStateMachine.RenderPaymentView && paymentMethod) && (React.createElement(PaymentView, { payeeName: payeeName, paymentMethod: paymentMethod, onPaymentBack: handlePaymentBack, onPaymentError: sendError, onPaymentConfirmed: handlePaymentConfirmed, stripeClientSecret: stripeClientSecret })),
        stateMachine === PaymentStateMachine.PaymentResult && (payment && (React.createElement("div", null,
            payment.status === PaymentStatus.Processing && (React.createElement("div", null,
                React.createElement(Box, { my: 2, style: { textAlign: 'center' } },
                    React.createElement(HourglassEmptyRoundedIcon, { fontSize: "large" })),
                React.createElement(Typography, { variant: "body1", align: "center" }, messages.PaymentInProcessing))),
            payment.status === PaymentStatus.Succeeded && (React.createElement("div", null,
                React.createElement(Box, { my: 2, style: { textAlign: 'center' } },
                    React.createElement(CheckCircleOutlineRoundedIcon, { fontSize: "large", style: styles.textSuccess })),
                React.createElement(Typography, { variant: "body1", align: "center" }, messages.PaymentSucceeded))),
            payment.status === PaymentStatus.Canceled && (React.createElement("div", null,
                React.createElement(Box, { my: 2, style: { textAlign: 'center' } },
                    React.createElement(HighlightOffIcon, { fontSize: "large", style: styles.textDanger })),
                React.createElement(Typography, { variant: "body1", align: "center" }, messages.PaymentCanceled))),
            PaymentProvider.Wallee === payment.provider && (payment.status === PaymentStatus.Processing || payment.status === PaymentStatus.Canceled) && (React.createElement(Fragment, null,
                React.createElement(Box, { my: 2 }),
                React.createElement(Alert, { severity: "warning" },
                    React.createElement(AlertTitle, null, messages.Warning),
                    React.createElement(Typography, null, messages.WalleePaymentRenewalHint),
                    React.createElement(Box, { my: 2 }),
                    React.createElement(Button, { size: "small", variant: "contained", onClick: handleWalleePaymentRenewal, disabled: inProgress }, messages.WalleePaymentRenewal)),
                React.createElement(Box, { my: 1 }),
                walleePaymentRenewalError && (React.createElement(Alert, { severity: "error" }, messages.WalleePaymentRenewalError)))),
            renderCloseButton()))),
        error && (React.createElement(Box, { mt: 3 }, renderErrorCode(error)))));
};
//# sourceMappingURL=PaymentFlow.js.map